import { captureException } from "@/lib/sentry";
import { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef, type ReactNode } from "react";

type NguTheme = "dark" | "light";

export const NGU_ACCENT_DEFAULT = "#22d3ee";
export const NGU_SECONDARY_DEFAULT = "#a78bfa";

interface NguThemeContextValue {
  theme: NguTheme;
  toggle: () => void;
  colors: typeof DARK_COLORS;
  accentColor: string;
  secondaryColor: string;
}

export const DARK_COLORS = {
  bg: "#080808",
  bgCard: "#0f0f0f",
  bgCardAlt: "#060606",
  bgInput: "#0d0d1a",
  border: "#1a1a3a",
  borderSubtle: "rgba(255,255,255,0.06)",
  text: "#e2e8f0",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  textHeading: "#fff",
  overlay: "rgba(0,0,0,0.85)",
  shadow: "rgba(0,0,0,0.4)",
};

export const LIGHT_COLORS = {
  bg: "#f8fafc",
  bgCard: "#ffffff",
  bgCardAlt: "#f1f5f9",
  bgInput: "#ffffff",
  border: "#e2e8f0",
  borderSubtle: "rgba(0,0,0,0.08)",
  text: "#1e293b",
  textMuted: "#475569",
  textDim: "#64748b",
  textHeading: "#0f172a",
  overlay: "rgba(0,0,0,0.3)",
  shadow: "rgba(0,0,0,0.1)",
};

const STORAGE_KEY = "ngu-admin-theme";

const NguThemeContext = createContext<NguThemeContextValue>({
  theme: "dark",
  toggle: () => {},
  colors: DARK_COLORS,
  accentColor: NGU_ACCENT_DEFAULT,
  secondaryColor: NGU_SECONDARY_DEFAULT,
});

export function useNguTheme() {
  return useContext(NguThemeContext);
}

// ── DOM walker helpers (work on computed rgb values — browser normalizes hex) ──

const domSaved = new WeakMap<HTMLElement, Map<string, string>>();

function parseRGBA(v: string): [number, number, number, number] | null {
  if (!v) return null;
  const m = v.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i);
  if (!m) return null;
  return [+m[1], +m[2], +m[3], m[4] !== undefined ? +m[4] : 1];
}

function luma(r: number, g: number, b: number): number {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function lightifyEl(el: HTMLElement): void {
  const s = el.style;

  const bgProps = ["background", "background-color"] as const;
  for (const p of bgProps) {
    const v = s.getPropertyValue(p);
    if (!v || v.includes("gradient") || v.includes("url(") || v.includes("linear-")) continue;
    if (v === LIGHT_COLORS.bg || v === LIGHT_COLORS.bgCard || v === LIGHT_COLORS.bgCardAlt) continue;
    const rgb = parseRGBA(v);
    if (!rgb) continue;
    const [r, g, b, a] = rgb;
    if (a < 0.1) continue;
    const sat = Math.max(r, g, b) - Math.min(r, g, b);
    const lu = luma(r, g, b);
    let lv: string | null = null;
    if (lu < 0.06 && sat < 35) lv = LIGHT_COLORS.bg;
    else if (lu < 0.15 && sat < 35) lv = LIGHT_COLORS.bgCard;
    if (lv) {
      let map = domSaved.get(el);
      if (!map) { map = new Map(); domSaved.set(el, map); }
      if (!map.has(p)) map.set(p, v);
      s.setProperty(p, lv, "important");
    }
  }

  const cv = s.getPropertyValue("color");
  if (cv && cv !== LIGHT_COLORS.textHeading && cv !== LIGHT_COLORS.text) {
    const rgb = parseRGBA(cv);
    if (rgb && luma(rgb[0], rgb[1], rgb[2]) > 0.82) {
      let map = domSaved.get(el);
      if (!map) { map = new Map(); domSaved.set(el, map); }
      if (!map.has("color")) map.set("color", cv);
      s.setProperty("color", LIGHT_COLORS.textHeading, "important");
    }
  }

  const bv = s.getPropertyValue("border-color");
  if (bv && bv !== LIGHT_COLORS.border) {
    const rgb = parseRGBA(bv);
    if (rgb && rgb[0] > 200 && rgb[1] > 200 && rgb[2] > 200 && rgb[3] < 0.2) {
      let map = domSaved.get(el);
      if (!map) { map = new Map(); domSaved.set(el, map); }
      if (!map.has("border-color")) map.set("border-color", bv);
      s.setProperty("border-color", LIGHT_COLORS.border, "important");
    }
  }
}

function darkenEl(el: HTMLElement): void {
  const map = domSaved.get(el);
  if (!map) return;
  for (const [p, v] of map) {
    el.style.removeProperty(p);
    if (v) el.style.setProperty(p, v);
  }
  domSaved.delete(el);
}

function walkTree(root: HTMLElement, fn: (el: HTMLElement) => void): void {
  fn(root);
  root.querySelectorAll<HTMLElement>("*").forEach(fn);
}

// ─────────────────────────────────────────────────────────────────────────────

export function NguThemeProvider({
  children,
  accentColor: accentProp,
  secondaryColor: secondaryProp,
}: {
  children: ReactNode;
  accentColor?: string;
  secondaryColor?: string;
}) {
  const accentColor = accentProp ?? NGU_ACCENT_DEFAULT;
  const secondaryColor = secondaryProp ?? NGU_SECONDARY_DEFAULT;
  const [theme, setTheme] = useState<NguTheme>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "light" || stored === "dark") return stored;
    } catch (_swallowedErr) { captureException(_swallowedErr); }
    return "dark";
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* intentionally silent */ }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme(t => (t === "dark" ? "light" : "dark"));
  }, []);

  const colors = theme === "dark" ? DARK_COLORS : LIGHT_COLORS;

  const rootRef = useRef<HTMLDivElement>(null);
  const moRef = useRef<MutationObserver | null>(null);
  const debRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // CSS overrides for form elements and Tailwind classes
  useEffect(() => {
    const id = "ngu-theme-overrides";
    let style = document.getElementById(id) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = id;
      document.head.appendChild(style);
    }
    if (theme === "light") {
      style.textContent = `
        .ngu-theme-root.ngu-light {
          background-color: ${LIGHT_COLORS.bg};
          color: ${LIGHT_COLORS.text};
          color-scheme: light;
        }
        .ngu-theme-root.ngu-light select,
        .ngu-theme-root.ngu-light input[type="text"],
        .ngu-theme-root.ngu-light input[type="number"],
        .ngu-theme-root.ngu-light input[type="email"],
        .ngu-theme-root.ngu-light input[type="url"],
        .ngu-theme-root.ngu-light input[type="search"],
        .ngu-theme-root.ngu-light input[type="password"],
        .ngu-theme-root.ngu-light textarea {
          background: ${LIGHT_COLORS.bgInput} !important;
          color: ${LIGHT_COLORS.text} !important;
          border-color: ${LIGHT_COLORS.border} !important;
        }
        .ngu-theme-root.ngu-light .bg-gray-950,
        .ngu-theme-root.ngu-light .bg-gray-900 { background-color: ${LIGHT_COLORS.bg} !important; }
        .ngu-theme-root.ngu-light .bg-gray-800,
        .ngu-theme-root.ngu-light .bg-gray-700 { background-color: ${LIGHT_COLORS.bgCard} !important; }
        .ngu-theme-root.ngu-light .text-white,
        .ngu-theme-root.ngu-light .text-gray-100 { color: ${LIGHT_COLORS.textHeading} !important; }
        .ngu-theme-root.ngu-light .text-gray-300,
        .ngu-theme-root.ngu-light .text-gray-200 { color: ${LIGHT_COLORS.textMuted} !important; }
        .ngu-theme-root.ngu-light .text-gray-400,
        .ngu-theme-root.ngu-light .text-gray-500 { color: ${LIGHT_COLORS.textDim} !important; }
        .ngu-theme-root.ngu-light .border-white\\/10,
        .ngu-theme-root.ngu-light .border-white\\/5 { border-color: ${LIGHT_COLORS.border} !important; }
        .ngu-theme-root.ngu-light .bg-white\\/5,
        .ngu-theme-root.ngu-light .bg-white\\/10 { background-color: rgba(0,0,0,0.04) !important; }
        .ngu-theme-root.ngu-light .text-white\\/50,
        .ngu-theme-root.ngu-light .text-white\\/40 { color: ${LIGHT_COLORS.textDim} !important; }
        .ngu-theme-root.ngu-light .text-white\\/60 { color: ${LIGHT_COLORS.textMuted} !important; }
        .ngu-theme-root.ngu-light .text-white\\/70,
        .ngu-theme-root.ngu-light .text-white\\/80 { color: ${LIGHT_COLORS.text} !important; }
      `;
    } else {
      style.textContent = `.ngu-theme-root { color-scheme: dark; }`;
    }
    return () => { if (style && style.parentNode) style.parentNode.removeChild(style); };
  }, [theme]);

  // JS DOM walker — runs synchronously (useLayoutEffect = no flash)
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    moRef.current?.disconnect();
    moRef.current = null;
    if (debRef.current) clearTimeout(debRef.current);

    if (theme === "light") {
      root.classList.add("ngu-light");
      walkTree(root, lightifyEl);

      const mo = new MutationObserver(() => {
        if (debRef.current) clearTimeout(debRef.current);
        debRef.current = setTimeout(() => {
          if (rootRef.current) walkTree(rootRef.current, lightifyEl);
        }, 80);
      });
      mo.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      });
      moRef.current = mo;
    } else {
      root.classList.remove("ngu-light");
      walkTree(root, darkenEl);
    }

    return () => {
      moRef.current?.disconnect();
      moRef.current = null;
    };
  }, [theme]);

  return (
    <NguThemeContext.Provider value={{ theme, toggle, colors, accentColor, secondaryColor }}>
      <div
        ref={rootRef}
        className="ngu-theme-root"
        style={{ "--ngu-accent": accentColor, "--ngu-secondary": secondaryColor } as React.CSSProperties}
      >
        {children}
      </div>
    </NguThemeContext.Provider>
  );
}

export function NguThemeToggle() {
  const { theme, toggle } = useNguTheme();
  const isDark = theme === "dark";

  return (
    <button
      onClick={toggle}
      data-testid="button-ngu-theme-toggle"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="transition-all duration-200 focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 focus-visible:outline-none"
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 9999,
        width: 40,
        height: 40,
        borderRadius: "50%",
        border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
        background: isDark ? "#1e293b" : "#ffffff",
        color: isDark ? "#fbbf24" : "#6366f1",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.12)",
      }}
    >
      {isDark ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5"/>
          <line x1="12" y1="1" x2="12" y2="3"/>
          <line x1="12" y1="21" x2="12" y2="23"/>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
          <line x1="1" y1="12" x2="3" y2="12"/>
          <line x1="21" y1="12" x2="23" y2="12"/>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      )}
    </button>
  );
}
